import React, { useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Smartphone, Globe, Mail, Phone, Brain, Database, Activity, Shield, Zap, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
// ============================================================================
// MOCK DATA
// ============================================================================
interface ChannelData {
  id: string;
  name: string;
  icon: 'chat' | 'mobile' | 'api' | 'email' | 'whatsapp';
  color: string;
  requestsPerDay: number;
}
interface KnowledgeData {
  id: string;
  name: string;
  documents: number;
  status: 'Attivo' | 'In aggiornamento';
}
interface LogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  status: 'success' | 'error';
  cost: number;
  latency: number;
}
interface ServiceDetailData {
  id: string;
  name: string;
  type: string;
  model: string;
  health: 'healthy' | 'warning' | 'critical';
  cost: number;
  compliance: number;
  requestsPerDay: number;
  tokens: string;
  description: string;
  channels: ChannelData[];
  knowledge: KnowledgeData[];
  logs: LogEntry[];
}
const SERVICE_DETAIL_DATA: Record<string, ServiceDetailData> = {
  s1: {
    id: 's1',
    name: 'Customer Assistant',
    type: 'Chatbot',
    model: 'GPT-4o',
    health: 'healthy',
    cost: 45,
    compliance: 92,
    requestsPerDay: 1250,
    tokens: '450K',
    description: 'Primary customer support agent handling L1 queries.',
    channels: [{
      id: 'ch1',
      name: 'Web Chat',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 680
    }, {
      id: 'ch2',
      name: 'WhatsApp',
      icon: 'whatsapp',
      color: '#25d366',
      requestsPerDay: 320
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 150
    }, {
      id: 'ch4',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'FAQ Database',
      documents: 142,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Catalog',
      documents: 89,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Return Policy',
      documents: 34,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:32:15',
      user: 'Marco B.',
      action: 'Richiesta info prodotto',
      status: 'success',
      cost: 0.08,
      latency: 1.2
    }, {
      id: 'l2',
      timestamp: '14:31:42',
      user: 'Sofia R.',
      action: 'Tracking ordine #4521',
      status: 'success',
      cost: 0.05,
      latency: 0.8
    }, {
      id: 'l3',
      timestamp: '14:30:18',
      user: 'Luca V.',
      action: 'Richiesta reso',
      status: 'success',
      cost: 0.12,
      latency: 1.5
    }, {
      id: 'l4',
      timestamp: '14:28:55',
      user: 'Anna N.',
      action: 'Domanda su garanzia',
      status: 'error',
      cost: 0.03,
      latency: 0.5
    }, {
      id: 'l5',
      timestamp: '14:27:30',
      user: 'Giovanni R.',
      action: 'Info spedizione',
      status: 'success',
      cost: 0.06,
      latency: 0.9
    }, {
      id: 'l6',
      timestamp: '14:25:12',
      user: 'Matteo L.',
      action: 'Problema pagamento',
      status: 'success',
      cost: 0.15,
      latency: 2.1
    }, {
      id: 'l7',
      timestamp: '14:22:45',
      user: 'Giulia P.',
      action: 'Info orari negozio',
      status: 'success',
      cost: 0.04,
      latency: 0.6
    }]
  },
  s2: {
    id: 's2',
    name: 'Smart Matcher',
    type: 'Matcher',
    model: 'GPT-4 Turbo',
    health: 'healthy',
    cost: 32,
    compliance: 88,
    requestsPerDay: 5500,
    tokens: '1.2M',
    description: 'Matches user intent to relevant knowledge base articles.',
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 4200
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 1100
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 200
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Knowledge Base',
      documents: 1250,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Documentation',
      documents: 340,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:32:45',
      user: 'System',
      action: 'Match query → KB article #892',
      status: 'success',
      cost: 0.02,
      latency: 0.4
    }, {
      id: 'l2',
      timestamp: '14:32:44',
      user: 'System',
      action: 'Match query → KB article #156',
      status: 'success',
      cost: 0.02,
      latency: 0.3
    }, {
      id: 'l3',
      timestamp: '14:32:43',
      user: 'System',
      action: 'No match found',
      status: 'error',
      cost: 0.01,
      latency: 0.2
    }, {
      id: 'l4',
      timestamp: '14:32:42',
      user: 'System',
      action: 'Match query → KB article #445',
      status: 'success',
      cost: 0.02,
      latency: 0.4
    }, {
      id: 'l5',
      timestamp: '14:32:41',
      user: 'System',
      action: 'Match query → KB article #1102',
      status: 'success',
      cost: 0.02,
      latency: 0.3
    }]
  },
  s3: {
    id: 's3',
    name: 'Content Moderator',
    type: 'Moderator',
    model: 'Claude 3.5',
    health: 'warning',
    cost: 18,
    compliance: 71,
    requestsPerDay: 800,
    tokens: '200K',
    description: 'Filters and flags inappropriate content in real-time.',
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 500
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 200
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Content Guidelines',
      documents: 56,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Blacklist Terms',
      documents: 1200,
      status: 'In aggiornamento'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:33:02',
      user: 'System',
      action: 'Content flagged: spam',
      status: 'success',
      cost: 0.04,
      latency: 0.8
    }, {
      id: 'l2',
      timestamp: '14:32:58',
      user: 'System',
      action: 'Content approved',
      status: 'success',
      cost: 0.03,
      latency: 0.7
    }, {
      id: 'l3',
      timestamp: '14:32:51',
      user: 'System',
      action: 'Content flagged: inappropriate',
      status: 'success',
      cost: 0.04,
      latency: 0.9
    }, {
      id: 'l4',
      timestamp: '14:32:45',
      user: 'System',
      action: 'Analysis timeout',
      status: 'error',
      cost: 0.01,
      latency: 5.0
    }, {
      id: 'l5',
      timestamp: '14:32:38',
      user: 'System',
      action: 'Content approved',
      status: 'success',
      cost: 0.03,
      latency: 0.6
    }]
  },
  s4: {
    id: 's4',
    name: 'AI Tutor',
    type: 'Tutor',
    model: 'GPT-4 Turbo',
    health: 'healthy',
    cost: 28,
    compliance: 95,
    requestsPerDay: 560,
    tokens: '300K',
    description: 'Internal training assistant for employee onboarding.',
    channels: [{
      id: 'ch1',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 380
    }, {
      id: 'ch2',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 150
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 30
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Training Materials',
      documents: 245,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Company Policies',
      documents: 67,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Onboarding Guide',
      documents: 23,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:30:12',
      user: 'Elena M.',
      action: 'Quiz completato: 85%',
      status: 'success',
      cost: 0.15,
      latency: 2.5
    }, {
      id: 'l2',
      timestamp: '14:28:45',
      user: 'Roberto F.',
      action: 'Domanda su policy HR',
      status: 'success',
      cost: 0.08,
      latency: 1.8
    }, {
      id: 'l3',
      timestamp: '14:25:30',
      user: 'Chiara B.',
      action: 'Richiesta materiale corso',
      status: 'success',
      cost: 0.06,
      latency: 1.2
    }, {
      id: 'l4',
      timestamp: '14:22:18',
      user: 'Marco P.',
      action: 'Sessione tutoring',
      status: 'success',
      cost: 0.22,
      latency: 4.5
    }, {
      id: 'l5',
      timestamp: '14:18:55',
      user: 'Laura S.',
      action: 'Feedback negativo',
      status: 'error',
      cost: 0.04,
      latency: 0.8
    }]
  }
};
const getDefaultServiceData = (id: string): ServiceDetailData => ({
  id,
  name: 'Generic Service',
  type: 'Assistant',
  model: 'GPT-4',
  health: 'healthy',
  cost: 0,
  compliance: 100,
  requestsPerDay: 0,
  tokens: '0',
  description: 'A generic AI service.',
  channels: [],
  knowledge: [],
  logs: []
});
// ============================================================================
// COMPONENT
// ============================================================================
const ChannelIcon = ({
  icon,
  className,
  style
}: {
  icon: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  switch (icon) {
    case 'chat':
      return <MessageSquare className={className} style={style} data-id="element-4556" data-mp="3jfra461i33qt" />;
    case 'mobile':
      return <Smartphone className={className} style={style} data-id="element-4557" data-mp="15nza3yo2jwe" />;
    case 'api':
      return <Globe className={className} style={style} data-id="element-4558" data-mp="3sfmpyo603mx6" />;
    case 'email':
      return <Mail className={className} style={style} data-id="element-4559" data-mp="3cg86x5f8bnh3" />;
    case 'whatsapp':
      return <Phone className={className} style={style} data-id="element-4560" data-mp="37nyad6jlzp5u" />;
    default:
      return <Globe className={className} style={style} data-id="element-4561" data-mp="37mn4902oy5bk" />;
  }
};
type TabType = 'overview' | 'canali' | 'knowledge' | 'log';
export function ServizioDetailPage() {
  const {
    agentId
  } = useParams<{
    agentId: string;
  }>();
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState<TabType>(screenInit.activeTab as TabType ?? 'overview');
  const service = useMemo(() => {
    return SERVICE_DETAIL_DATA[agentId || ''] || getDefaultServiceData(agentId || 'unknown');
  }, [agentId]);
  const healthColor = service.health === 'healthy' ? 'text-[#8eff01]' : service.health === 'warning' ? 'text-amber-500' : 'text-red-500';
  const healthBg = service.health === 'healthy' ? 'bg-[#8eff01]' : service.health === 'warning' ? 'bg-amber-500' : 'bg-red-500';
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'canali',
    label: 'Canali'
  }, {
    id: 'knowledge',
    label: 'Knowledge'
  }, {
    id: 'log',
    label: 'Log'
  }];
  return <div className="min-h-screen bg-[#111114] pt-8 px-8 pb-12 space-y-8" data-id="element-4562" data-mp="25tn40d6jccbs">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-white/40" data-id="element-4563" data-mp="2ajttsgcsa2vm">
        <button onClick={() => navigate('/canvas')} className="hover:text-white transition-colors" data-id="element-4564" data-mp="2am6g15pkqzeh">
          AI Map
        </button>
        <span data-id="element-4565" data-mp="2jk8q0arad2dw">/</span>
        <span className="text-white/60" data-id="element-4566" data-mp="2jjqoaylutvyl">{service.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-1" data-id="element-4567" data-mp="2okwgmvblrh7l">
        <div className="flex items-center gap-3" data-id="element-4568" data-mp="2ohs2b54fb537">
          <h1 className="text-[24px] font-semibold text-white tracking-tight" data-id="element-4569" data-mp="2tj7ncx6lpbll">
            {service.name}
          </h1>
          <div className="flex items-center gap-2 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06]" data-id="element-4570" data-mp="u7epkybzo27t">
            <div className={`w-1.5 h-1.5 rounded-full ${healthBg}`} data-id="element-4571" data-mp="z712t7rthigm" />
            <span className="text-[11px] font-medium text-white/60 capitalize" data-id="element-4572" data-mp="r50hz79cnyxu">
              {service.health}
            </span>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] font-medium text-white/60" data-id="element-4573" data-mp="112ewv2fmkfo1">
            {service.type}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] font-medium text-white/60" data-id="element-4574" data-mp="w5eqeqev5awe">
            {service.model}
          </span>
        </div>
        <p className="text-[14px] text-white/40 max-w-2xl" data-id="element-4575" data-mp="153qf2mw65ym7">
          {service.description}
        </p>
      </div>

      {/* Metrics Row */}
      <div className="flex items-center gap-8 py-2" data-id="element-4576" data-mp="1eu39r7xp7862">
        <div data-id="element-4577" data-mp="1eulc8mayeimf">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-4578" data-mp="1evwdaed0lc9u">
            Costo Mensile
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-4579" data-mp="1os985fkapw66">
            €{service.cost}/mo
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-4580" data-mp="27g1kdv1atp9k" />
        <div data-id="element-4581" data-mp="27e6wq8yn7j1s">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-4582" data-mp="2cfe6gdkcisb3">
            Richieste/Giorno
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-4583" data-mp="1yg4u5oz9gice">
            {service.requestsPerDay.toLocaleString()}
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-4584" data-mp="23cv743frqxtl" />
        <div data-id="element-4585" data-mp="1opa387b2kukm">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-4586" data-mp="1ooqwlmqvdslw">
            Compliance
          </p>
          <p className={`text-[18px] font-semibold ${service.compliance >= 90 ? 'text-[#8eff01]' : 'text-amber-500'}`} data-id="element-4587" data-mp="1ti39izm6sayt">
            {service.compliance}%
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-4588" data-mp="1jrqpd8iaajt7" />
        <div data-id="element-4589" data-mp="1joclfvtz22jn">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-4590" data-mp="15r7lmdkupzwu">
            Token Usage
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-4591" data-mp="1aqagx6b14rrm">
            {service.tokens}
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-white/[0.08]" data-id="element-4592" data-mp="2d59m5dlqi6za">
        <div className="flex gap-1 overflow-x-auto pb-1" data-id="element-4593" data-mp="2934v8mmo7ogc">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)} className={`
                flex items-center px-4 py-2.5 text-[13px] font-medium transition-all rounded-lg whitespace-nowrap relative
                ${activeTab === tab.id ? 'text-white bg-white/[0.06]' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.02]'}
              `} data-id="element-4594" data-mp="24allk0r3tdgt">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]" data-id="element-4595" data-mp="1le3u7kg63fl3">
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-4596" data-mp="1geqvo2fxok2o">
            <div className="space-y-6" data-id="element-4597" data-mp="3fqjxqdhkwfm9">
              <div data-id="element-4598" data-mp="3fpil44kwzl2f">
                <h3 className="text-[14px] font-medium text-white mb-4" data-id="element-4599" data-mp="3fnxpebmv5w2z">
                  Dettagli Servizio
                </h3>
                <div className="space-y-4" data-id="element-4600" data-mp="3ib86i66x7smc">
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4601" data-mp="3iag9qs1l7ouh">
                    <span className="text-[13px] text-white/45" data-id="element-4602" data-mp="3n88dcjblgzei">
                      ID Servizio
                    </span>
                    <span className="text-[13px] text-white/75 font-mono" data-id="element-4603" data-mp="3s2vrppcvwdfd">
                      {service.id}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4604" data-mp="2zd5ls3leohuc">
                    <span className="text-[13px] text-white/45" data-id="element-4605" data-mp="34c02mzztbwbt">
                      Modello AI
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4606" data-mp="398qhjjzvslvn">
                      {service.model}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4607" data-mp="okc8tdg3ijx8">
                    <span className="text-[13px] text-white/45" data-id="element-4608" data-mp="sl4lkgo6f6y9">
                      Tipo Agente
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4609" data-mp="2pkzcascirvjh">
                      {service.type}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4610" data-mp="2kpa9mg57dyiy">
                    <span className="text-[13px] text-white/45" data-id="element-4611" data-mp="2ykcm5m4k2m4q">
                      Ultimo Aggiornamento
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4612" data-mp="25xje0iou6kkp">2 ore fa</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-4613" data-mp="21ud513acafaj">
              <div data-id="element-4614" data-mp="2fo4d078f9r45">
                <h3 className="text-[14px] font-medium text-white mb-4" data-id="element-4615" data-mp="2fomizvkvzjp9">
                  Performance
                </h3>
                <div className="space-y-4" data-id="element-4616" data-mp="2avt9yvidxp7d">
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4617" data-mp="1n71pkqb1eb55">
                    <span className="text-[13px] text-white/45" data-id="element-4618" data-mp="1i8qekc909ulc">
                      Uptime (30gg)
                    </span>
                    <span className="text-[13px] text-[#8eff01]" data-id="element-4619" data-mp="3tur7vkjsmuzc">99.98%</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4620" data-mp="2i6at41b1ihx">
                    <span className="text-[13px] text-white/45" data-id="element-4621" data-mp="3kw6xd6odzrm7">
                      Latenza Media
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4622" data-mp="3pugsxie0wu8c">0.8s</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4623" data-mp="hex734fome3b">
                    <span className="text-[13px] text-white/45" data-id="element-4624" data-mp="lfh0py11qzkp">
                      Tasso di Errore
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4625" data-mp="7ilo5vll6pzw">0.05%</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-4626" data-mp="ch5cg2nnkdvf">
                    <span className="text-[13px] text-white/45" data-id="element-4627" data-mp="2s8jhg4y29p5w">
                      Feedback Positivi
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-4628" data-mp="3osup3pybirc3">94%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'canali' && <div className="max-w-3xl" data-id="element-4629" data-mp="3euoorcs5tt9c">
            <div className="space-y-0" data-id="element-4630" data-mp="3evq3pscaqzwc">
              {service.channels.map(channel => <div key={channel.id} className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-id="element-4631" data-mp="39wn8ezm4c81k">
                  <div className="flex items-center gap-3" data-id="element-4632" data-mp="bcwt9oeter6e">
                    <div className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: channel.color
              }} data-id="element-4633" data-mp="6feuuryfbucn" />
                    <ChannelIcon icon={channel.icon} className="w-4 h-4 text-white/40" data-id="element-4634" data-mp="1n3szjk4hyhg" />
                    <span className="text-[13px] text-white/70" data-id="element-4635" data-mp="3stogitayyo9q">
                      {channel.name}
                    </span>
                  </div>
                  <span className="text-[13px] text-white/40 font-mono" data-id="element-4636" data-mp="u6kab60gckmf">
                    {channel.requestsPerDay.toLocaleString()} req/day
                  </span>
                </div>)}
            </div>
          </div>}

        {activeTab === 'knowledge' && <div className="max-w-3xl" data-id="element-4637" data-mp="11p9xv7g6tas6">
            <div className="space-y-0" data-id="element-4638" data-mp="11qbctn0bqhf6">
              {service.knowledge.map(kb => <div key={kb.id} className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-id="element-4639" data-mp="16mjtx5y30koz">
                  <div className="flex items-center gap-3" data-id="element-4640" data-mp="1bnpsvlic546y">
                    <Brain className="w-4 h-4 text-purple-400/70" data-id="element-4641" data-mp="1gmarxhluphtt" />
                    <span className="text-[13px] text-white/70" data-id="element-4642" data-mp="1kn30s9qt9kpk">{kb.name}</span>
                  </div>
                  <div className="flex items-center gap-6" data-id="element-4643" data-mp="1pdtau0a6mbql">
                    <span className="text-[13px] text-white/40 flex items-center gap-1.5" data-id="element-4644" data-mp="1pel3p3ce9vd6">
                      <Database className="w-3 h-3" data-id="element-4645" data-mp="1ucea9xhm0rdz" />
                      {kb.documents} doc
                    </span>
                    <div className="flex items-center gap-1.5" data-id="element-4646" data-mp="1z9wa8ydnfw9u">
                      <div className={`w-1.5 h-1.5 rounded-full ${kb.status === 'Attivo' ? 'bg-[#8eff01]' : 'bg-amber-500'}`} data-id="element-4647" data-mp="24bvc23tqql4o" />
                      <span className={`text-[12px] ${kb.status === 'Attivo' ? 'text-[#8eff01]' : 'text-amber-500'}`} data-id="element-4648" data-mp="32vsmsyf0i54b">
                        {kb.status}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

        {activeTab === 'log' && <div data-id="element-4649" data-mp="3hom2ajuaal8z">
            <table className="w-full text-left border-collapse" data-id="element-4650" data-mp="3lsk5b5mqcg7b">
              <thead data-id="element-4651" data-mp="3lsaclaaavuxx">
                <tr className="border-b border-white/[0.10]" data-id="element-4652" data-mp="3qrn8ekj5gccn">
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-4653" data-mp="3qq2f0y8kmzgd">
                    Ora
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-4654" data-mp="4bspumwthnej">
                    Utente
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-4655" data-mp="9cq4wqmj61bt">
                    Azione
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-4656" data-mp="1xvav7z7k0nty">
                    Stato
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider text-right" data-id="element-4657" data-mp="1trwet7fpe5fz">
                    Costo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]" data-id="element-4658" data-mp="1jx4h83m4fil7">
                {service.logs.map(log => <tr key={log.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-4659" data-mp="2hhz3reobmvad">
                    <td className="py-2.5 text-[13px] text-white/40 font-mono" data-id="element-4660" data-mp="2ck7d0nzexf7r">
                      {log.timestamp}
                    </td>
                    <td className="py-2.5 text-[13px] text-white/70" data-id="element-4661" data-mp="22tvv1xiw95hv">
                      {log.user}
                    </td>
                    <td className="py-2.5 text-[13px] text-white/60" data-id="element-4662" data-mp="309jhjajsoz3q">
                      {log.action}
                    </td>
                    <td className="py-2.5" data-id="element-4663" data-mp="2vgytyqrhfi8n">
                      <span className={`text-[12px] font-medium ${log.status === 'success' ? 'text-[#8eff01]' : 'text-red-500'}`} data-id="element-4664" data-mp="tl9f3yy089fs">
                        {log.status === 'success' ? 'OK' : 'Errore'}
                      </span>
                    </td>
                    <td className="py-2.5 text-[13px] text-white/40 text-right font-mono" data-id="element-4665" data-mp="juoiui5szh01">
                      €{log.cost.toFixed(2)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}